import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { GetTenantAction } from '@/application/actions';
import { useToast } from '@/presentation/contexts/ToastContext';
import { usePlans } from '@/presentation/hooks/usePlans';
import { Button, Input, ModalShell } from '@/presentation/components/common';
import type { Tenant } from '@/domain/entities/Tenant';
import type { CollectionsDebtorDTO } from '@/domain/repositories/IReportRepository';
import type { IPanelPagoRepository } from '@/domain/repositories/IPanelPagoRepository';
import { PanelPago } from '@/domain/entities/PanelPago';
import { TenantCommercialPacksSection } from '@/presentation/components/tenants/TenantCommercialPacksSection';

type TabKey = 'datos' | 'historial';

interface ViewTenantModalProps {
  isOpen: boolean;
  tenantId: string | number;
  debtor?: CollectionsDebtorDTO | null;
  onClose: () => void;
}

function statusBadgeClass(activo: boolean) {
  return activo
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
    : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
}

function paymentStatusBadgeClass(estado: string) {
  if (estado === 'Abonado' || estado === 'Abonado con Mora') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
  }
  if (estado === 'Mora') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
  }
  if (estado === 'Impago' || estado === 'Pendiente pago') {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200';
  }
  if (estado === 'Gratis' || estado === 'Bonificado') {
    return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200';
  }
  return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200';
}

export const ViewTenantModal: React.FC<ViewTenantModalProps> = ({
  isOpen,
  tenantId,
  debtor,
  onClose,
}) => {
  const { t } = useTranslation();
  const { error: showError, success } = useToast();
  const { plans } = usePlans();
  const [tab, setTab] = useState<TabKey>('datos');
  const [isLoading, setIsLoading] = useState(true);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [payments, setPayments] = useState<PanelPago[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openingReciboId, setOpeningReciboId] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTab('datos');
    setSearchTerm('');
    setPayments([]);
    setPaymentsLoaded(false);
  }, [isOpen, tenantId]);

  useEffect(() => {
    if (!isOpen || tenantId == null || tenantId === '') {
      setTenant(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const action = container.get<GetTenantAction>(TYPES.GetTenantAction);
        const data = await action.execute(tenantId);
        if (!cancelled) setTenant(data);
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : t('pages.collections.tenantLoadError'));
          onClose();
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, tenantId]);

  useEffect(() => {
    if (!isOpen || tab !== 'historial' || paymentsLoaded || !tenant) return;

    let cancelled = false;
    const load = async () => {
      setPaymentsLoading(true);
      try {
        const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
        const tenantPayments = await repository.getByTenantId(tenant.id.getValue());
        tenantPayments.sort(
          (a, b) =>
            new Date(b.fechaPeriodoPago).getTime() - new Date(a.fechaPeriodoPago).getTime()
        );
        if (!cancelled) {
          setPayments(tenantPayments);
          setPaymentsLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          showError(err instanceof Error ? err.message : t('pages.tenants.paymentHistory'));
        }
      } finally {
        if (!cancelled) setPaymentsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, tab, paymentsLoaded, tenant]);

  const planName = useMemo(() => {
    const planId =
      tenant?.planAdminId ?? (debtor?.planAdminId ? Number(debtor.planAdminId) : undefined);
    if (!planId) return '—';
    return plans.find((p) => p.id === planId)?.nombre || String(planId);
  }, [tenant, debtor, plans]);

  const filteredPayments = useMemo(() => {
    if (!searchTerm) return payments;
    const term = searchTerm.toLowerCase();
    return payments.filter(
      (payment) =>
        payment.fechaPeriodoPago.toLowerCase().includes(term) ||
        payment.estadoPagoDescripcion.toLowerCase().includes(term) ||
        payment.monto.toString().includes(term) ||
        (payment.notas && payment.notas.toLowerCase().includes(term))
    );
  }, [payments, searchTerm]);

  const totalRecaudado = filteredPayments
    .filter((p) => p.fechaPago)
    .reduce((sum, p) => sum + p.monto, 0);
  const totalPendiente = filteredPayments
    .filter((p) => !p.fechaPago)
    .reduce((sum, p) => sum + p.monto, 0);

  const openRecibo = async (paymentId: number) => {
    setOpeningReciboId(paymentId);
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const html = await repository.getReciboHtml(paymentId);
      const win = window.open('', '_blank');
      if (!win) {
        showError(t('pages.tenants.reciboPopupBlocked'));
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      success(t('pages.tenants.reciboOpened'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.tenants.reciboError'));
    } finally {
      setOpeningReciboId(null);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      className="rounded-2xl"
      showCloseButton={false}
      headerClassName="!p-0"
      bodyClassName="!px-5 !py-5 sm:!px-6"
      footer={
        <Button type="button" variant="secondary" onClick={onClose} className="w-full sm:w-auto">
          {t('common.close')}
        </Button>
      }
      header={
        <div className="relative w-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-teal-950/40" />
          <div className="relative flex items-start justify-between gap-4 px-5 py-5 sm:px-6">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wider text-teal-700 dark:text-teal-300">
                {t('pages.collections.clinicDetail')}
              </p>
              {isLoading ? (
                <div className="mt-2 h-7 w-48 animate-pulse rounded bg-slate-200 dark:bg-gray-700" />
              ) : (
                <h3 className="mt-1 truncate text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl dark:text-white">
                  {tenant?.nombre || '—'}
                </h3>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {tenant && (
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(tenant.isActive())}`}
                  >
                    {tenant.isActive()
                      ? t('pages.tenants.active')
                      : t('pages.tenants.inactive')}
                  </span>
                )}
                <span className="inline-flex rounded-full bg-slate-900/5 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-white/10 dark:text-slate-200">
                  {t('pages.tenants.plan')}: {planName}
                </span>
                {debtor && (
                  <span className="inline-flex rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950/50 dark:text-red-300">
                    {t('pages.collections.totalDebt')}: $
                    {debtor.totalAdeudado.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-900/5 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
          <div className="relative flex gap-1 px-5 sm:px-6">
            {(
              [
                { key: 'datos' as const, label: t('pages.collections.tabData') },
                { key: 'historial' as const, label: t('pages.tenants.paymentHistory') },
              ] as const
            ).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`relative px-3 py-2.5 text-sm font-medium transition ${
                  tab === item.key
                    ? 'text-teal-700 dark:text-teal-300'
                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                {item.label}
                {tab === item.key && (
                  <span className="absolute inset-x-1 -bottom-px h-0.5 rounded-full bg-teal-600 dark:bg-teal-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      }
    >
            {isLoading ? (
              <div className="py-16 text-center text-slate-500 dark:text-slate-400">
                {t('common.loading')}
              </div>
            ) : tenant && tab === 'datos' ? (
              <div className="space-y-5">
                {debtor && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-xl border border-red-100 bg-red-50/80 p-3 dark:border-red-900/40 dark:bg-red-950/30">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-red-700/80 dark:text-red-300/80">
                        {t('pages.collections.totalDebt')}
                      </p>
                      <p className="mt-1 text-lg font-semibold tabular-nums text-red-700 dark:text-red-300">
                        ${debtor.totalAdeudado.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('pages.collections.status')}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                        {debtor.estadoPago}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('pages.collections.daysOverdue')}
                      </p>
                      <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                        {debtor.daysOverdue}
                      </p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                        {t('pages.collections.periods')}
                      </p>
                      <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-white">
                        {debtor.periods.length}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    {
                      label: t('pages.tenants.email'),
                      value: tenant.emailAdministrador.getValue(),
                    },
                    {
                      label: t('pages.tenants.phone'),
                      value: debtor?.telefonoContacto || '—',
                    },
                    { label: t('pages.tenants.plan'), value: planName },
                    {
                      label: t('pages.tenants.billingCurrency'),
                      value:
                        tenant.moneda === 'USD'
                          ? t('pages.tenants.currencyUSD')
                          : t('pages.tenants.currencyARS'),
                    },
                    { label: t('pages.tenants.database'), value: tenant.baseDeDatos },
                    { label: t('pages.tenants.timeZone'), value: tenant.timeZoneId },
                    {
                      label: t('pages.tenants.activeDate'),
                      value: new Date(tenant.fechaActivoUTC).toLocaleDateString('es-AR'),
                    },
                    {
                      label: t('pages.tenants.expirationDate'),
                      value: tenant.fechaVencimiento
                        ? new Date(tenant.fechaVencimiento).toLocaleDateString('es-AR')
                        : '—',
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-gray-700 dark:bg-gray-900/30"
                    >
                      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {item.label}
                      </p>
                      <p className="mt-1 break-words text-sm font-medium text-slate-900 dark:text-white">
                        {item.value || '—'}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-teal-200/70 bg-teal-50/30 p-3.5 dark:border-teal-900 dark:bg-teal-950/20">
                  {tenant.moneda === 'USD' && (
                    <p className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200">
                      {t('pages.tenants.usdCommercialHint')}
                    </p>
                  )}
                  <TenantCommercialPacksSection
                    tenantId={tenant.id.getValue()}
                    tenantName={tenant.nombre}
                    readOnly
                  />
                  <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                    Para asignar o quitar packs: Clínicas → menú ⋮ → Editar.
                  </p>
                </div>
              </div>
            ) : tenant && tab === 'historial' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/30">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-700/80 dark:text-emerald-300/80">
                      {t('pages.tenants.totalCollected')}
                    </p>
                    <p className="mt-1 text-lg font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                      ${totalRecaudado.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-xl border border-amber-100 bg-amber-50/80 p-3 dark:border-amber-900/40 dark:bg-amber-950/30">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-amber-700/80 dark:text-amber-300/80">
                      {t('pages.tenants.totalPending')}
                    </p>
                    <p className="mt-1 text-lg font-semibold tabular-nums text-amber-700 dark:text-amber-300">
                      ${totalPendiente.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-xl border border-teal-100 bg-teal-50/80 p-3 dark:border-teal-900/40 dark:bg-teal-950/30">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-teal-700/80 dark:text-teal-300/80">
                      {t('pages.tenants.totalPayments')}
                    </p>
                    <p className="mt-1 text-lg font-semibold tabular-nums text-teal-700 dark:text-teal-300">
                      {filteredPayments.length}
                    </p>
                  </div>
                </div>

                <Input
                  label={t('common.search')}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('pages.tenants.searchPayments')}
                />

                {paymentsLoading ? (
                  <div className="py-10 text-center text-slate-500 dark:text-slate-400">
                    {t('common.loading')}
                  </div>
                ) : filteredPayments.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-slate-500 dark:border-gray-700 dark:text-slate-400">
                    {t('pages.tenants.noPayments')}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-gray-700">
                      <thead className="bg-slate-50 dark:bg-gray-900">
                        <tr>
                          <th className="px-3 py-2.5 text-left text-xs font-medium uppercase text-slate-500">
                            {t('pages.tenants.paymentPeriod')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-medium uppercase text-slate-500">
                            {t('pages.tenants.paymentDate')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-medium uppercase text-slate-500">
                            {t('pages.tenants.amount')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-medium uppercase text-slate-500">
                            {t('pages.tenants.paymentMethod')}
                          </th>
                          <th className="px-3 py-2.5 text-left text-xs font-medium uppercase text-slate-500">
                            {t('pages.tenants.status')}
                          </th>
                          <th className="px-3 py-2.5 text-right text-xs font-medium uppercase text-slate-500">
                            {t('pages.tenants.receipt')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-gray-700/80">
                        {filteredPayments.map((payment) => (
                          <tr
                            key={payment.id}
                            className="hover:bg-slate-50/80 dark:hover:bg-gray-900/40"
                          >
                            <td className="whitespace-nowrap px-3 py-2.5 text-slate-900 dark:text-white">
                              {payment.fechaPeriodoPago}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-slate-500 dark:text-slate-400">
                              {payment.fechaPago
                                ? new Date(payment.fechaPago).toLocaleDateString('es-AR')
                                : '—'}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums font-medium text-slate-900 dark:text-white">
                              ${payment.monto.toLocaleString()}
                            </td>
                            <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">
                              {payment.medioPagoDescripcion || '—'}
                            </td>
                            <td className="px-3 py-2.5">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${paymentStatusBadgeClass(payment.estadoPagoDescripcion)}`}
                              >
                                {payment.estadoPagoDescripcion}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={openingReciboId === payment.id}
                                onClick={() => openRecibo(payment.id)}
                                title={t('pages.tenants.openRecibo')}
                              >
                                {openingReciboId === payment.id ? '…' : '📄'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
    </ModalShell>
  );
};

